import api from '@/lib/api'
import type { PaginatedResponse } from '@/types'

export interface AlertType {
  id: string
  domain_id: string
  name: string
  slug: string
  priority: 1 | 2 | 3
  interval_seconds: number
  is_active: boolean
  settings: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface Alert {
  id: number
  domain_id: string
  vehicle_id: string
  device_id: string
  alert_type_id: string
  /** Joined by API when available */
  alert_type?: AlertType
  triggered_at: string
  received_at: string
  position: { lat: number; lng: number } | null
  speed: number | null
  data: Record<string, unknown> | null
  /** 1=high, 2=medium, 3=low */
  priority: 1 | 2 | 3
  is_read: boolean
  read_by: string | null
  read_at: string | null
  /** Reverse-geocoded address of the alert position (filled async after creation) */
  address?: string | null
  /** Flat join fields returned by some API responses */
  alert_type_name?: string
  vehicle_name?: string
  registration_number?: string
}

export interface AlertListParams {
  from?: string
  to?: string
  vehicle_id?: string
  alert_type_id?: string
  priority?: 1 | 2 | 3
  is_read?: boolean
  page?: number
  per_page?: number
}

// ── Alert Types ────────────────────────────────────────────────────────────────

export const listAlertTypes = () =>
  api
    .get<{ success: true; data: AlertType[] }>('/alert-types')
    .then((r) => r.data.data)

export const getAlertType = (id: string) =>
  api
    .get<{ success: true; data: AlertType }>(`/alert-types/${id}`)
    .then((r) => r.data.data)

export const createAlertType = (body: {
  name: string
  slug: string
  priority?: 1 | 2 | 3
  interval_seconds?: number
  settings?: Record<string, unknown>
}) =>
  api
    .post<{ success: true; data: AlertType }>('/alert-types', body)
    .then((r) => r.data.data)

export const updateAlertType = (
  id: string,
  body: {
    name?: string
    is_active?: boolean
    priority?: 1 | 2 | 3
    interval_seconds?: number
    settings?: Record<string, unknown>
  },
) =>
  api
    .put<{ success: true; data: AlertType }>(`/alert-types/${id}`, body)
    .then((r) => r.data.data)

export const deleteAlertType = (id: string) =>
  api.delete(`/alert-types/${id}`).then((r) => r.data)

// ── Alert Events ───────────────────────────────────────────────────────────────

export const listAlerts = (params?: AlertListParams) =>
  api
    .get<PaginatedResponse<Alert>>('/alerts', { params })
    .then((r) => r.data)

export const getUnreadCount = () =>
  api
    .get<{ success: true; data: { count: number } }>('/alerts/unread-count')
    .then((r) => r.data.data.count)

export const markAlertsRead = (alertIds: number[]) =>
  api.post('/alerts/mark-read', { alert_ids: alertIds }).then((r) => r.data)

export const markAllAlertsRead = () =>
  api.post('/alerts/mark-read', { mark_all: true }).then((r) => r.data)
